import { useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { JobAssignmentSummary } from './JobAssignmentSummary.js';

interface ClarifyResult {
  readonly clarificationId: string;
  readonly raisedAt: string;
}

/**
 * M08-005 §11 — Raise a clarification against an assignment.
 * POST /v1/vendor/assignments/:assignmentId/clarify {subject, questionText}.
 * Subject options come from the backend ClarificationSubject enum.
 */
const SUBJECTS = ['SITE', 'AREA', 'SCOPE', 'TIMELINE', 'MATERIAL', 'COMMERCIAL'] as const;

export function VendorJobClarifyPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [subject, setSubject] = useState<(typeof SUBJECTS)[number]>('SITE');
  const [questionText, setQuestionText] = useState('');
  const [result, setResult] = useState<ClarifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function raise() {
    setError(null);
    if (!questionText.trim()) {
      setError(t('vops.jobClarify.questionRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<ClarifyResult>(`/v1/vendor/assignments/${assignmentId}/clarify`, {
        method: 'POST',
        token,
        body: { subject, questionText: questionText.trim() },
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="vendor-job-clarify">
      <h1>{t('vops.jobClarify.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      <JobAssignmentSummary assignmentId={assignmentId} />

      {error ? <p role="alert">{error}</p> : null}
      {result ? (
        <p role="status" aria-label="clarify-result">{t('vops.jobClarify.result')}</p>
      ) : null}

      <label htmlFor="clarify-subject">{t('vops.jobClarify.subject')}</label>
      <select
        id="clarify-subject"
        aria-label="clarify-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value as (typeof SUBJECTS)[number])}
        disabled={busy || result !== null}
      >
        {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      <label htmlFor="clarify-question">{t('vops.jobClarify.question')}</label>
      <textarea
        id="clarify-question"
        aria-label="clarify-question"
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        disabled={busy || result !== null}
      />

      <Button
        type="button"
        aria-label="clarify-btn"
        onClick={raise}
        disabled={busy || result !== null}
        variant="secondary"
      >
        {t('vops.jobClarify.raiseBtn')}
      </Button>
    </main>
  );
}
